import APP_CONFIG from '@/config/app';
import {
  WalletInterface,
  WalletTokenInterface,
  WalletUnderlyingYieldBearingAssetInterface,
} from '@/interfaces';
import { formatToSentenceCase, sanitizeAssetAmount } from '@/utils/formatUtils';
import {
  generateLogoUri,
  generateTokenId,
  generateWalletKey,
} from '@/utils/functionalUtils';

export const normalizeWalletsResponse = (
  response: any[],
): WalletInterface[] => {
  const tokenColors = APP_CONFIG.PORTFOLIO_TOKEN_COLORS;
  const uniqueColors = new Set<string>();
  let colorIndex = 0;

  const assignUniqueColor = () => {
    let color = `${tokenColors[colorIndex % tokenColors.length]} bg-opacity-80`;
    while (uniqueColors.has(color)) {
      colorIndex = (colorIndex + 1) % tokenColors.length;
      color = `${tokenColors[colorIndex]} bg-opacity-80`;
    }
    uniqueColors.add(color);
    colorIndex++;
    return color;
  };

  const calculateBalanceInUsd = (asset: any) => {
    const balanceInUsd = parseFloat(asset.balanceInUsd);
    if (balanceInUsd > 0) {
      return balanceInUsd;
    }

    const balance = parseFloat(asset.balance);
    const priceInUsd = parseFloat(asset.priceInUsd);
    if (balance > 0 && priceInUsd > 0) {
      return balance * priceInUsd;
    }

    return 0;
  };

  response.forEach((wallet: any) => {
    wallet.balances.forEach((token: any) => {
      token.balanceInUsd = calculateBalanceInUsd(token);
    });
    wallet.nativeAssetBalances.forEach((asset: any) => {
      asset.balanceInUsd = calculateBalanceInUsd(asset);
    });
  });

  const totalPortfolioValueInUsd = response.reduce(
    (sum: number, wallet: any) =>
      sum +
      wallet.balances.reduce(
        (balanceSum: number, token: any) =>
          balanceSum + parseFloat(token.balanceInUsd || 0),
        0,
      ) +
      wallet.nativeAssetBalances.reduce(
        (assetSum: number, asset: any) =>
          assetSum + parseFloat(asset.balanceInUsd || 0),
        0,
      ),
    0,
  );

  return response.map((wallet: any) => {
    const { address, blockchain, userId, nativeAssetBalances = [] } = wallet;
    const walletKey = generateWalletKey({ blockchain, address });
    const name = formatToSentenceCase(blockchain);
    const walletBlockchainDisplayName =
      APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
        blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
      ] || blockchain;
    const aggregatedBalanceInUsd =
      wallet.balances.reduce(
        (sum: number, token: any) => sum + parseFloat(token.balanceInUsd || 0),
        0,
      ) +
      nativeAssetBalances.reduce(
        (sum: number, asset: any) => sum + parseFloat(asset.balanceInUsd || 0),
        0,
      );

    const enrichedBalances: WalletTokenInterface[] = wallet.balances.map(
      (token: any): WalletTokenInterface => ({
        ...generateTokenId({
          ...token,
          blockchainDisplayName:
            APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
              token.blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
            ] || token.blockchain,
          balance: sanitizeAssetAmount(token.balance),
          priceInUsd: sanitizeAssetAmount(token.priceInUsd),
          balanceInUsd: parseFloat(token.balanceInUsd),
          walletName: walletBlockchainDisplayName,
          walletAddress: address,
          walletBlockchain: blockchain,
          walletKey,
          walletBlockchainDisplayName,
        }),
        logoUri: generateLogoUri(token.symbol, token.logoUri),
        color: assignUniqueColor(),
        shareOfPortfolio:
          totalPortfolioValueInUsd > 0
            ? parseFloat(token.balanceInUsd || 0) / totalPortfolioValueInUsd
            : 0,
      }),
    );

    const enrichedUnderlyingYieldBearingAssets: WalletUnderlyingYieldBearingAssetInterface[] =
      nativeAssetBalances.map(
        (asset: any): WalletUnderlyingYieldBearingAssetInterface => {
          const enrichedAsset: WalletUnderlyingYieldBearingAssetInterface = {
            ...generateTokenId({
              ...asset,
              isUnderlyingYieldBearingAsset: true,
              blockchainDisplayName:
                APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
                  asset.blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
                ] || asset.blockchain,
              balance: sanitizeAssetAmount(asset.balance),
              rewards: sanitizeAssetAmount(asset.rewards),
              priceInUsd: parseFloat(asset.priceInUsd),
              balanceInUsd: sanitizeAssetAmount(asset.balanceInUsd),
              rewardsInUsd: sanitizeAssetAmount(asset.rewardsInUsd),
              walletName: walletBlockchainDisplayName,
              walletAddress: address,
              walletBlockchain: blockchain,
              walletKey,
              walletBlockchainDisplayName,
            }),
            logoUri: generateLogoUri(asset.symbol, asset.logoUri),
            color: assignUniqueColor(),
            shareOfPortfolio:
              totalPortfolioValueInUsd > 0
                ? parseFloat(asset.balanceInUsd || 0) / totalPortfolioValueInUsd
                : 0,
          };

          if (asset.underlyingAssets) {
            enrichedAsset.underlyingAssets = asset.underlyingAssets.map(
              (underlyingAsset: any): WalletTokenInterface => ({
                ...generateTokenId({
                  ...underlyingAsset,
                  blockchainDisplayName:
                    APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
                      underlyingAsset.blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
                    ] || underlyingAsset.blockchain,
                  balance: sanitizeAssetAmount(underlyingAsset.balance),
                  priceInUsd: sanitizeAssetAmount(
                    underlyingAsset.priceInUsd,
                  ),
                  balanceInUsd: parseFloat(underlyingAsset.balanceInUsd),
                  walletName: walletBlockchainDisplayName,
                  walletAddress: address,
                  walletBlockchain: blockchain,
                  walletKey,
                  walletBlockchainDisplayName,
                }),
                logoUri: generateLogoUri(
                  underlyingAsset.symbol,
                  underlyingAsset.logoUri,
                ),
                color: assignUniqueColor(),
                shareOfPortfolio:
                  totalPortfolioValueInUsd > 0
                    ? parseFloat(underlyingAsset.balanceInUsd || 0) /
                      totalPortfolioValueInUsd
                    : 0,
              }),
            );
          }

          return enrichedAsset;
        },
      );

    return {
      name,
      address,
      blockchain,
      walletKey,
      balances: enrichedBalances,
      underlyingYieldBearingAssetBalances:
        enrichedUnderlyingYieldBearingAssets,
      aggregatedBalanceInUsd,
      userId,
      blockchainDisplayName: walletBlockchainDisplayName,
      userPlanInfo: wallet.userPlanInfo,
    };
  });
};
